import { Link, useParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import PageHero from '@/components/feature/PageHero';
import { diseases, hospitals } from '@/mocks/home';
import { fetchDiseaseInfo, fetchDiseaseList } from '@/services/disease';
import { adaptDisease, adaptDiseases } from '@/services/adapters';
import { resolveHtmlAssetUrls } from '@/services/request';
import { useRequest } from '@/hooks/useRequest';
import type { DiseaseHospitalViewItem, DiseaseItem } from '@/types/content';

export default function DiseaseDetailPage() {
  const { id } = useParams();
  const diseaseId = Number(id);

  // 详情接口失败时降级为 mock 数据
  const { data: disease, loading } = useRequest<DiseaseItem | undefined>(
    () => fetchDiseaseInfo(diseaseId).then((res) => adaptDisease(res.dataInfo)),
    { fallback: diseases.find((item) => item.id === diseaseId), deps: [diseaseId] },
  );

  // 相关病种列表（详情接口不返回，取列表接口排除当前病种）
  const { data: diseaseList } = useRequest<DiseaseItem[]>(
    () => fetchDiseaseList({ pageIndex: 1, pageSize: 999 }).then((res) => adaptDiseases(res.list)),
    { fallback: diseases },
  );

  if (loading && !disease) {
    return (
      <PageShell>
        <PageHero
          eyebrow="优势病种"
          title="正在加载病种信息"
          breadcrumb={[{ label: '首页', to: '/' }, { label: '优势病种', to: '/diseases' }, { label: '病种详情' }]}
        />
        <section className="w-full px-4 md:px-8 py-24 flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </section>
      </PageShell>
    );
  }

  if (!disease) {
    return (
      <PageShell>
        <PageHero
          eyebrow="优势病种"
          title="病种信息不存在"
          description="抱歉，未找到该病种信息，可能链接有误。"
          breadcrumb={[{ label: '首页', to: '/' }, { label: '优势病种', to: '/diseases' }, { label: '病种详情' }]}
        />
        <section className="w-full px-4 md:px-8 py-16 text-center">
          <Link
            to="/diseases"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回优势病种
          </Link>
        </section>
      </PageShell>
    );
  }

  const related = diseaseList.filter((item) => item.id !== disease.id).slice(0, 3);

  // 关联医院：优先取详情接口返回的 hospitalList；mock 兜底时从 mock 医院数据计算
  const coveringHospitals: DiseaseHospitalViewItem[] = disease.hospitalList?.length
    ? disease.hospitalList
    : hospitals
        .map((hospital) => ({
          hospitalId: hospital.id,
          name: hospital.name,
          image: hospital.image,
          planNames: hospital.advantageDiseases
            .filter((plan) => plan.diseaseId === disease.id)
            .map((plan) => plan.name),
        }))
        .filter((entry) => entry.planNames.length > 0);

  return (
    <PageShell>
      <PageHero
        eyebrow="优势病种"
        title={disease.name}
        breadcrumb={[{ label: '首页', to: '/' }, { label: '优势病种', to: '/diseases' }, { label: '病种详情' }]}
      />

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-7xl mx-auto">
          <div>
            <div className="w-full aspect-[1920/810] rounded-lg overflow-hidden border border-background-200">
              <img
                src={disease.image}
                alt={disease.name}
                title={`${disease.name} - 优势病种`}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="mt-8 flex items-start gap-3 p-5 rounded-lg bg-accent-50 border border-accent-100">
              <span className="shrink-0 w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-heart-pulse-line text-lg"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-accent-800 mb-1">主要症状</p>
                <p className="text-sm text-foreground-700 leading-relaxed">{disease.symptom}</p>
              </div>
            </div>

            <h2 className="mt-10 flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
              <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
              辨证与调理
            </h2>
            <div className="mt-4 space-y-4">
              {disease.detail.map((paragraph, index) =>
                paragraph.includes('<') ? (
                  <div
                    key={index}
                    className="rich-text"
                    dangerouslySetInnerHTML={{ __html: resolveHtmlAssetUrls(paragraph) }}
                  />
                ) : (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {paragraph}
                  </p>
                ),
              )}
            </div>

            <p className="mt-6 text-xs text-foreground-400">
              以上内容仅为疾病知识科普，具体诊疗请遵从专业医师指导。
            </p>
          </div>
        </div>

        {coveringHospitals.length > 0 && (
          <div className="max-w-7xl mx-auto mt-14 md:mt-20">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">关联医院</h2>
              <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                {coveringHospitals.length} 家
              </span>
            </div>
            <p className="text-sm text-foreground-500 mb-6">以下医院的特色诊疗方案覆盖该病种</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {coveringHospitals.map((item) => (
                <Link
                  key={item.hospitalId}
                  to={`/hospitals/${item.hospitalId}`}
                  className="group flex flex-col sm:flex-row rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="w-full sm:w-[200px] h-[160px] sm:h-auto overflow-hidden shrink-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      title={`${item.name} - 关联医院`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex flex-col flex-1 p-5">
                    <h3 className="font-heading font-semibold text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors">
                      {item.name}
                    </h3>
                    <p className="mt-3 text-xs text-foreground-500">覆盖该病种的特色方案</p>
                    <ul className="mt-2 flex flex-col gap-1.5">
                      {item.planNames.map((planName) => (
                        <li
                          key={planName}
                          className="flex items-start gap-1.5 text-xs text-foreground-600 leading-relaxed"
                        >
                          <i className="ri-checkbox-circle-line text-accent-600 mt-0.5 shrink-0"></i>
                          <span className="line-clamp-2">{planName}</span>
                        </li>
                      ))}
                    </ul>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600">
                      查看医院详情
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto mt-14 md:mt-20">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">相关优势病种</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/diseases/${item.id}`}
                className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="w-full h-[150px] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    title={`${item.name} - 优势病种`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                    {item.name}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}